import React, { useEffect } from 'react';
import { PDFDocument, rgb, PDFFont, PDFPage } from 'pdf-lib';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { useNavigate, useParams } from 'react-router-dom';
import { getEntity } from './foreigner-residence.reducer';
import fontkit from '@pdf-lib/fontkit';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { translate } from 'react-jhipster';

/**
 * Formats an ISO date string into Thai date
 * (Thai numerals, Thai month, Buddhist Era year).
 * Example: 2026-01-19 -> ๑๙ มกราคม ๒๕๖๙
 */
const formatDateBEThai = (isoDateStr: string): string => {
  try {
    const jsDate = new Date(isoDateStr);
    if (isNaN(jsDate.getTime())) return '';

    const thaiMonths = [
      'มกราคม',
      'กุมภาพันธ์',
      'มีนาคม',
      'เมษายน',
      'พฤษภาคม',
      'มิถุนายน',
      'กรกฎาคม',
      'สิงหาคม',
      'กันยายน',
      'ตุลาคม',
      'พฤศจิกายน',
      'ธันวาคม',
    ];
    const day = formatThaiNumber(jsDate.getDate());
    const monthName = thaiMonths[jsDate.getMonth()];
    const yearBE = formatThaiNumber(jsDate.getFullYear() + 543);
    return `${day} ${monthName} ${yearBE}`;
  } catch {
    return '';
  }
};

/**
 * Convert only Arabic digits to Thai digits.
 * Other characters will remain unchanged.
 * Example: "25 มกราคม 2569" -> "๒๕ มกราคม ๒๕๖๙"
 */
const formatThaiNumber = (value: string | number): string => {
  const thaiDigits = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙'];

  return value.toString().replace(/\d/g, digit => thaiDigits[Number(digit)]);
};

/**
 * Draws text on the given PDF page at specified coordinates with the provided font settings.
 */
const drawText = (page: PDFPage, text: string, x: number, y: number, font: PDFFont, fontSize = 14, color = rgb(0, 0, 0)) => {
  page.drawText(text || '', { x, y, size: fontSize, font, color });
};

/**
 * Finds a user object from the list by ID, and returns their full name.
 */
const getUserName = (users: any[], userId: number | undefined): string => {
  if (!userId) return '';
  const user = users.find(u => u.id === userId);
  if (!user) return '';
  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  return `${firstName} ${lastName}`.trim();
};

/**
 * Finds a user object from the list by ID, and returns their title.
 */
const getUserTitle = (users: any[], userId: number | undefined): string => {
  if (!userId) return '';
  const user = users.find(u => u.id === userId);
  return user?.title || '';
};

/**
 * Finds a user object from the list by ID, and returns their title.
 */
const getUserTitle2 = (users: any[], userId: number | undefined): string => {
  if (!userId) return '';
  const user = users.find(u => u.id === userId);
  return user?.title2 || '';
};

const drawForeignerRequestText = (
  page: PDFPage,
  fullName: string,
  residencePurpose: string,
  x1: number,
  x2: number,
  y: number,
  maxWidth: number,
  font: PDFFont,
  fontSize: number,
  lineHeight = 18,
): number => {
  const line2 = 'ประสงค์ขอให้ออกหนังสือการรับแจ้งที่พักอาศัย';
  const line3 = 'ในประเทศไทย เพื่อประกอบการขอมี' + residencePurpose + ' นั้น';

  // บรรทัดแรก: ตามที่ + ชื่อ (ต้องอยู่ด้วยกันเสมอ)
  const firstLine = `ตามที่ ${fullName}`;
  const firstLineWidth = font.widthOfTextAtSize(firstLine, fontSize);

  if (firstLineWidth <= maxWidth) {
    // ✔ ชื่อพอดีบรรทัดแรก
    drawText(page, firstLine + ' ' + line2, x1, y, font, fontSize);
    drawText(page, line3, x2, y - lineHeight, font, fontSize);
    return 2;
  }

  // ✔ ชื่อยาว → แยกเป็น 3 บรรทัด
  drawText(page, firstLine, x1, y, font, fontSize);
  drawText(page, line2, x2, y - lineHeight, font, fontSize);
  drawText(page, line3, x2, y - lineHeight * 2, font, fontSize);
  return 3;
};

/**
 * Fills content on the first page of the PDF.
 * - วาดข้อมูลหลักของใบรับแจ้งที่พักอาศัย
 * - ตำแหน่งและรูปแบบข้อความถูกปรับให้เหมาะสมกับฟอร์ม PDF
 */
const fillFirstPage = (page: PDFPage, entity: any, users: any[], thaiFont: PDFFont, maxHeight: number) => {
  const fontSize = 16;
  const indentX1 = 136; // ตำแหน่ง X หลัก
  const indentX2 = 78; // ตำแหน่ง X รอง
  const X_Center = 303; // ตำแหน่ง X กึ่งกลาง

  // วาดเลขรับเรื่อง
  drawText(page, formatThaiNumber(entity.recordNumber) || '', 168, maxHeight - 125, thaiFont, fontSize);
  // วาดวันที่รับเรื่อง
  const formattedCreateDate = entity.createDate ? formatDateBEThai(entity.createDate) : '';
  drawText(page, formattedCreateDate, X_Center, maxHeight - 188, thaiFont, fontSize);

  // เตรียมชื่อเต็มสำหรับข้อความหลัก
  const fullName = [entity.foreignerTitle, entity.foreignerFullName].filter(Boolean).join(' ').toUpperCase();

  // วาดคำนำหน้าชื่อและชื่อเต็ม
  const titleX = 105;
  const titleWidth = thaiFont.widthOfTextAtSize(entity.foreignerTitle || '', fontSize);
  drawText(page, fullName, titleX, maxHeight - 237, thaiFont, fontSize);

  // วาดข้อความคำร้อง
  const startY = maxHeight - 262;
  let purposeText = translate(`foreignerResidenceApp.ResidencePurpose.${entity.purpose}`);
  if (entity.purpose === 'DRIVER_LICENSE') {
    purposeText = 'ใบอนุญาตขับขี่';
  } else if (entity.purpose === 'OTHER') {
    purposeText = entity.purposeOther;
  }

  const usedLines = drawForeignerRequestText(page, fullName, purposeText, indentX1, indentX2, startY, 380, thaiFont, fontSize, 18);

  // สัญลักษณ์ checkbox (ใช้ 'X' แทน ☒ เพราะฟอนต์ไม่รองรับ)
  const checked = 'X';
  const unchecked = '☐';
  // รองรับค่าหลายรูปแบบจาก backend
  const isChecked = v => v === true || v === 'true' || v === 1 || v === '1';
  const condition37 = isChecked(entity.condition37);
  const condition38 = isChecked(entity.condition38);

  // วาดข้อความมาตรา 37(5) และ 38
  const text1 = `ตรวจคนเข้าเมืองจังหวัดสุราษฎร์ธานี ได้รับแจ้งข้อมูลตาม`;
  drawText(page, text1, indentX1, maxHeight - 300, thaiFont, fontSize);
  drawText(page, `${condition37 ? checked : unchecked}  (มาตรา ๓๗(๕))`, 380, maxHeight - 300, thaiFont, fontSize);
  drawText(page, `${condition38 ? checked : unchecked}  (มาตรา ๓๘)`, 465, maxHeight - 300, thaiFont, fontSize);

  // วาดข้อความอธิบายเพิ่มเติม
  const text2 = `แห่งพระราชบัญญัติคนเข้าเมือง พ.ศ.๒๕๒๒ ว่า ${fullName} ถือหนังสือเดินทางประเทศ`;
  drawText(page, text2, indentX2, maxHeight - 320, thaiFont, fontSize);

  // วาดข้อมูลสัญชาติและเลขหนังสือเดินทาง
  const nationality = translate(`foreignerResidenceApp.Nationality.${entity.nationality}`).toUpperCase();
  const foreignerPassportNo = formatThaiNumber(entity.foreignerPassportNo) || '';
  const formattedRecordDate = entity.recordDate ? formatDateBEThai(entity.recordDate) : '';
  const text3 = `/สัญชาติ ${nationality} เลขที่ ${foreignerPassportNo} ได้รับอนุญาตให้อยู่ในราชอาณาจักร ถีงวันที่ ${formattedRecordDate}`;
  drawText(page, text3, indentX2, maxHeight - 338, thaiFont, fontSize);

  // วาดที่อยู่
  const address1 = [
    formatThaiNumber(entity.houseNo) ? `พักอาศัยอยู่ เลขที่ ${formatThaiNumber(entity.houseNo)}` : '',
    formatThaiNumber(entity.moo) ? `หมู่ ${formatThaiNumber(entity.moo)}` : '',
    entity.district ? `ตำบล ${translate(`foreignerResidenceApp.District.${entity.district}`)}` : '',
    entity.city ? `อำเภอ ${translate(`foreignerResidenceApp.City.${entity.city}`)}` : '',
    entity.province ? `จังหวัด ${translate(`foreignerResidenceApp.Province.${entity.province}`)}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  drawText(page, address1, indentX2, maxHeight - 356, thaiFont, fontSize);

  // วาดข้อความปิดท้าย
  const text4 = `จึงเรียนมาเพื่อโปรดทราบ`;
  drawText(page, text4, indentX1, maxHeight - 374, thaiFont, fontSize);
  const text5 = `ขอแสดงความนับถือ`;
  drawText(page, text5, X_Center, maxHeight - 442, thaiFont, fontSize);

  // วาดชื่อและตำแหน่งเจ้าหน้าที่
  const officerName = `( ${getUserName(users, entity.officer?.id)} )`;
  const officerTitle = getUserTitle(users, entity.officer?.id);
  const officerTitle2 = getUserTitle2(users, entity.officer?.id);
  const officerNameX = X_Center - officerName.length / 2;
  const officerTitleX = officerNameX - officerTitle.length * 5;
  drawText(page, officerTitle, officerTitleX, maxHeight - 498, thaiFont, fontSize);
  drawText(page, officerName, officerNameX, maxHeight - 516, thaiFont, fontSize);

  // วาดตำแหน่งเจ้าหน้าที่
  const text6 = officerTitle2;
  drawText(page, text6, 260, maxHeight - 534, thaiFont, fontSize);
};

export const ForeignerResidencePdfExport = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  useEffect(() => {
    if (id) {
      dispatch(getEntity(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getUsers({}));
  }, [dispatch]);

  const foreignerResidenceEntity = useAppSelector(state => state.foreignerResidence.entity);
  const users = useAppSelector(state => state.userManagement.users);

  useEffect(() => {
    if (foreignerResidenceEntity && foreignerResidenceEntity.id != null && Array.isArray(users) && users.length > 0) {
      generateFilledPdf();
      navigate('/foreigner-residence');
    }
  }, [foreignerResidenceEntity, users, navigate]);

  const generateFilledPdf = async () => {
    try {
      const [existingPdfBytes, fontBytes] = await Promise.all([
        fetch('/content/templates/foreigner-residence-template1.pdf').then(res => res.arrayBuffer()),
        fetch('/content/fonts/THSarabun.ttf').then(res => res.arrayBuffer()),
      ]);
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      pdfDoc.registerFontkit(fontkit);
      const thaiFont = await pdfDoc.embedFont(fontBytes);

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width: pageWidth, height: pageHeight } = firstPage.getSize();
      const maxHeight = pageHeight;

      fillFirstPage(firstPage, foreignerResidenceEntity, users, thaiFont, maxHeight);

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return <></>;
};

export default ForeignerResidencePdfExport;
